import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useActiveOrg } from '../lib/activeOrg';
import { fetchTypePipelines } from '../lib/workItemPipelines';
import { Button, Modal, Toast } from '../components/primitives';
import { UNTITLED_WORK_ITEM_TITLE } from '../lib/editor/defaultTitles';
import { EditorCover, type EditorCoverTarget } from '../components/EditorCover';
import {
  WorkItemsView,
  useUrlWorkItemsViewState
} from '../components/work-items/view';
import './WorkItems.css';

const NEW_WORK_ITEM_DEFAULT_STATUS = 'Backlog';

type CreatableWorkItemType = 'epic' | 'story' | 'bug' | 'task';

const CREATABLE_TYPES: ReadonlyArray<{
  key: CreatableWorkItemType;
  label: string;
  description: string;
}> = [
  {
    key: 'epic',
    label: 'Epic',
    description:
      'Top-level container for related stories, bugs, and tasks. Has its own planning window (start / end / due).'
  },
  {
    key: 'story',
    label: 'Story',
    description: 'A user-facing capability that can be linked to an Epic.'
  },
  {
    key: 'bug',
    label: 'Bug',
    description: 'Something broken that needs fixing.'
  },
  {
    key: 'task',
    label: 'Task',
    description: 'A standalone unit of work.'
  }
];

type CreateState = 'idle' | 'creating';

/**
 * `/work-items` — Board (default) · Table · Timeline.
 *
 * This route is a thin host: it owns the page header (+ New work item), the
 * create-work-item flow, the inline-edit failure Toast, and the EditorCover.
 * The actual tabs + control bar + data tabs live in the universal
 * <WorkItemsView>, shared with the Project-detail Work Items section. Here the
 * view state is URL-backed (deep-links + reload round-trip); on the project
 * page it's component-local.
 */
export function WorkItems() {
  // The active org is owned by the shell (see `useActiveOrg`). This page is a
  // pure consumer: it scopes every query to whatever org the shell says is
  // active and re-loads when the user switches. It must NOT re-derive its own
  // "oldest membership" org — that was the cross-org leak.
  const { activeOrgId } = useActiveOrg();
  const [canCreate, setCanCreate] = useState(false);
  const [createState, setCreateState] = useState<CreateState>('idle');
  const [createError, setCreateError] = useState<string | null>(null);
  // Inline-edit failure toast. Lives at the route level (not inside the
  // Table tab) so it persists across tab switches — matches Documents.tsx.
  const [toast, setToast] = useState<string | null>(null);

  const viewState = useUrlWorkItemsViewState();

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const { data: sessionData } = await supabase.auth.getSession();
      if (cancelled) return;
      const session = sessionData.session;
      if (!session) return;

      // No active org yet — the shell is still resolving which org to land in.
      // Stay as-is; this effect re-runs once it resolves.
      if (!activeOrgId) return;

      // Capability for THIS org's membership (gates the + New button).
      const { data: memRow, error: memErr } = await supabase
        .from('memberships')
        .select('permission_role_id')
        .eq('user_id', session.user.id)
        .eq('org_id', activeOrgId)
        .maybeSingle();
      if (cancelled || memErr) return;

      const permissionRoleId =
        (memRow as { permission_role_id: string | null } | null)
          ?.permission_role_id ?? null;
      if (!permissionRoleId) return;
      const { data: pg } = await supabase
        .from('permission_groups')
        .select('system_key, capability_keys')
        .eq('id', permissionRoleId)
        .maybeSingle();
      if (cancelled || !pg) return;
      const systemKey = (pg as { system_key: string | null }).system_key;
      const keys =
        (pg as { capability_keys: string[] | null }).capability_keys ?? [];
      const isPrivileged = systemKey === 'owner' || systemKey === 'admin';
      setCanCreate(isPrivileged || keys.includes('tasks.create'));
    })();
    return () => {
      cancelled = true;
    };
  }, [activeOrgId]);

  const [typePickerOpen, setTypePickerOpen] = useState(false);
  // The slide-up editor cover. Opening a work item keeps this list mounted
  // (scroll + filters preserved) instead of navigating to the detail route.
  // `reloadSignal` bumps on close so the active tab refetches in place.
  const [cover, setCover] = useState<EditorCoverTarget | null>(null);
  const [reloadSignal, setReloadSignal] = useState(0);
  const openItem = useCallback(
    (id: string) => setCover({ kind: 'workItem', id }),
    []
  );

  const onCreate = useCallback(
    async (type: CreatableWorkItemType) => {
      if (createState === 'creating') return;
      if (!activeOrgId || !canCreate) return;
      setCreateState('creating');
      setCreateError(null);

      // Default to the per-type pipeline's first stage (e.g. Epic → Proposed),
      // falling back to the shared pipeline, falling back to the hard-coded
      // 'Backlog' default if RLS hides both pipeline rows.
      const { byType, error: pipeErr } = await fetchTypePipelines(
        [type, null],
        activeOrgId
      );
      if (pipeErr) {
        setCreateState('idle');
        setCreateError(pipeErr);
        return;
      }
      const stages = byType.get(type) ?? byType.get(null);
      const firstStage =
        stages && stages.length > 0 ? stages[0] : NEW_WORK_ITEM_DEFAULT_STATUS;

      // `create_work_item` sets `created_by` server-side from the caller, so
      // there's no need to thread the session user id through here.
      const { data, error: insertErr } = await supabase.rpc('create_work_item', {
        p_org_id: activeOrgId,
        p_type: type,
        p_name: UNTITLED_WORK_ITEM_TITLE,
        p_status: firstStage
      });
      const envelope = data as { ok?: boolean; work_item?: { id?: string } } | null;
      if (insertErr || !envelope?.ok || !envelope.work_item?.id) {
        setCreateState('idle');
        setCreateError(
          insertErr?.message ?? "Couldn't create the work item. Please try again."
        );
        return;
      }
      const newId = envelope.work_item.id;
      setTypePickerOpen(false);
      setCreateState('idle');
      // All types — including Epic — open in the same editor cover; the Epic
      // branch of WorkItemDetail handles type-specific UI.
      setCover({ kind: 'workItem', id: newId });
    },
    [activeOrgId, canCreate, createState]
  );

  return (
    <div
      className="work-items-route"
      data-testid="work-items-route"
      data-tab={viewState.tab}
      data-can-create={canCreate ? '1' : '0'}
      data-create-state={createState}
    >
      <header className="work-items-route__header">
        <h1 className="work-items-route__title">Work Items</h1>
        <div className="work-items-route__header-actions">
          {canCreate ? (
            <Button
              variant="primary"
              size="sm"
              disabled={createState === 'creating' || !activeOrgId}
              data-testid="work-items-new-btn"
              onClick={() => setTypePickerOpen(true)}
            >
              + New work item
            </Button>
          ) : null}
        </div>
      </header>
      {createError ? (
        <div
          className="work-items-route__error"
          role="alert"
          data-testid="work-items-new-error"
        >
          {createError}
        </div>
      ) : null}
      <WorkItemsView
        state={viewState}
        activeOrgId={activeOrgId}
        reloadSignal={reloadSignal}
        onOpenItem={openItem}
        onToast={setToast}
        testIdPrefix="work-items-route"
        controlBarTestIdPrefix="work-items"
      />
      <Modal
        open={typePickerOpen}
        onClose={() => {
          if (createState === 'creating') return;
          setTypePickerOpen(false);
        }}
        title="What kind of work item?"
        dataTestId="work-items-new-type-picker"
      >
        <div
          className="work-items-route__type-picker"
          data-testid="work-items-new-type-picker-body"
        >
          {CREATABLE_TYPES.map((t) => (
            <button
              key={t.key}
              type="button"
              className="work-items-route__type-picker-option"
              data-testid={`work-items-new-type-picker-${t.key}`}
              data-type={t.key}
              disabled={createState === 'creating'}
              onClick={() => void onCreate(t.key)}
            >
              <span className="work-items-route__type-picker-label">
                {t.label}
              </span>
              <span className="work-items-route__type-picker-desc">
                {t.description}
              </span>
            </button>
          ))}
        </div>
      </Modal>
      {toast ? (
        <Toast onDismiss={() => setToast(null)}>{toast}</Toast>
      ) : null}

      <EditorCover
        target={cover}
        onNavigate={setCover}
        onClose={() => {
          setCover(null);
          // Refetch the active tab in place (no remount → scroll preserved).
          setReloadSignal((n) => n + 1);
        }}
      />
    </div>
  );
}
